import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { createSubscription, cancelSubscription, loadRazorpayScript, openRazorpayCheckout, activateRazorpaySubscription } from '../services/razorpay';
import { cancelPayPalSubscription, startPayPalCheckout, confirmPayPalSubscription } from '../services/paypal';

import { getPricingRegion, getRegionalPricing, INDIA_PRO_AVAILABLE } from '../utils/plans';
import { hasPaidAccess, toDate } from '../utils/entitlements';
import Breadcrumbs from '../Components/Breadcrumbs';
import Footer from '../Components/Footer';
import './labcss/pricing.css';

// Feature lists
const PLANS = {
  free: {
    id: 'free',
    name: 'Free',
    description: 'Get started with basic soap making tools',
    features: [
      { text: 'Lye Calculator (NaOH & KOH)', included: true },
      { text: 'Up to 10 saved recipes', included: true },
      { text: '30 ingredients in inventory', included: true },
      { text: 'Label generator', included: false },
      { text: 'Community forum access', included: true },
      { text: 'Public recipe browser', included: true },
      { text: 'Unlimited recipes', included: false },
      { text: 'Advanced recipe generator', included: false },
      { text: 'Up to 5 batch records & curing calendar', included: true },
      { text: 'Sales dashboard', included: false },
      { text: 'Data backup & export', included: false },
      { text: 'Priority support', included: false },
    ]
  },
  pro: {
    id: 'pro',
    name: 'Pro',
    description: 'Everything you need for serious soap makers',
    features: [
      { text: 'Lye Calculator (NaOH & KOH)', included: true },
      { text: 'Unlimited saved recipes', included: true },
      { text: 'Unlimited ingredients', included: true },
      { text: 'Advanced label generator', included: true },
      { text: 'Community forum access', included: true },
      { text: 'Public recipe browser', included: true },
      { text: 'Smart recipe generator', included: true },
      { text: 'Full batch tracking', included: true },
      { text: 'Sales dashboard & analytics', included: true },
      { text: 'Data backup & export', included: true },
      { text: 'Recipe templates library', included: true },
      { text: 'Priority email support', included: true },
    ]
  }
};

const FEATURE_COMPARISON = [
  { feature: 'Lye Calculator', free: 'NaOH & KOH', pro: 'NaOH & KOH + Advanced' },
  { feature: 'Saved Recipes', free: '10', pro: 'Unlimited' },
  { feature: 'Inventory Items', free: '30', pro: 'Unlimited' },
  { feature: 'Batch Records', free: '5', pro: 'Unlimited' },
  { feature: 'Label Generator', free: '—', pro: '✓' },
  { feature: 'Recipe Generator', free: '—', pro: '✓' },
  { feature: 'Sales Dashboard', free: '—', pro: '✓' },
  { feature: 'Data Backup', free: '—', pro: '✓' },
  { feature: 'Analytics & Reports', free: '—', pro: '✓' },
  { feature: 'Support', free: 'Community', pro: 'Priority Email' },
];

export default function PricingPage() {
  const { user, userProfile, fetchUserProfile } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [showSuccessView, setShowSuccessView] = useState(false);
  // Pro members see their plan summary until they choose to view the plans.
  const [viewingPlans, setViewingPlans] = useState(false);

  const region = getPricingRegion();
  // Pro checkout: PayPal for customers abroad; Razorpay in India once its account is approved.
  const proOnSale = region === 'international' || INDIA_PRO_AVAILABLE;

  const currentPlan = userProfile?.plan || 'free';
  const subscriptionStatus = userProfile?.subscriptionStatus;
  const subscriptionEndDate = toDate(userProfile?.subscriptionEndDate || userProfile?.planExpiry);
  const isPro = hasPaidAccess(userProfile);

  // Poll the returned profile, with one bounded timer and cleanup on navigation.
  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    if (!paymentStatus) return undefined;
    if (paymentStatus === 'failed' || paymentStatus === 'cancelled') {
      setMessage({ type: 'error', text: 'Payment was not completed. Please try again.' });
      window.history.replaceState({}, '', '/pricing');
      return undefined;
    }
    if (paymentStatus !== 'success' || !user) return undefined;
    const paypalSubscriptionId = searchParams.get('provider') === 'paypal' ? searchParams.get('subscription_id') : null;
    let cancelled = false;
    let timer;
    let attempts = 0;
    setMessage({ type: 'info', text: 'Verifying your payment...' });
    const pollSubscription = async () => {
      // Confirm with PayPal directly: its first payment can take a few seconds to record,
      // and the webhook may arrive after the customer does, so this retries with the poll.
      if (paypalSubscriptionId) await confirmPayPalSubscription(paypalSubscriptionId).catch(() => {});
      const profile = await fetchUserProfile(user.uid);
      if (cancelled) return;
      if (hasPaidAccess(profile)) {
        setShowSuccessView(true);
        setMessage(null);
        window.history.replaceState({}, '', '/pricing');
      } else if (++attempts < 12) {
        timer = setTimeout(pollSubscription, 5000);
      } else {
        setMessage({ type: 'info', text: 'Payment confirmation is still pending. You can check back here shortly.' });
        window.history.replaceState({}, '', '/pricing');
      }
    };
    pollSubscription();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [searchParams, user, fetchUserProfile]);

  // Poll for subscription updates when user has a pending subscription
  useEffect(() => {
    if ((userProfile?.pendingSubscriptionId || userProfile?.pendingPayPalSubscriptionId) && user && !isPro) {
      setMessage({ type: 'info', text: 'Waiting for payment confirmation...' });
      
      const pollInterval = setInterval(async () => {
        await fetchUserProfile(user.uid);
      }, 5000); // Check every 5 seconds
      
      // Stop polling after 2 minutes
      const timeout = setTimeout(() => {
        clearInterval(pollInterval);
        setMessage(null);
      }, 120000);
      
      return () => {
        clearInterval(pollInterval);
        clearTimeout(timeout);
      };
    }
  }, [userProfile?.pendingSubscriptionId, userProfile?.pendingPayPalSubscriptionId, user, isPro, fetchUserProfile]);

  // Detect when user becomes Pro (from polling or page refresh)
  useEffect(() => {
    if (isPro && !showSuccessView && userProfile?.subscriptionId) {
      // User just became Pro, show success
      setShowSuccessView(true);
      setMessage(null);
    }
  }, [isPro, showSuccessView, userProfile?.subscriptionId]);

  // Show success view if user just became Pro
  useEffect(() => {
    if (isPro && message?.type === 'success') {
      setShowSuccessView(true);
    }
  }, [isPro, message]);

  const getPricing = getRegionalPricing;

  const getPrice = (plan) => {
    if (plan === 'free') return 'Free';
    const pricing = getPricing();
    const price = billingCycle === 'yearly' ? pricing.yearly.price : pricing.monthly.price;
    return `${pricing.monthly.currency}${price.toLocaleString()}`;
  };

  const getMonthlyEquivalent = () => {
    const pricing = getPricing();
    return `${pricing.monthly.currency}${pricing.yearly.monthlyEquivalent}`;
  };

  // Handle Razorpay subscription
  const handleRazorpaySubscribe = async () => {
    if (!user) {
      navigate('/signup?redirect=/pricing');
      return;
    }

    if (isPro) {
      setMessage({ type: 'info', text: 'You already have an active Pro subscription!' });
      return;
    }

    setLoading(true);
    setMessage({ type: 'info', text: 'Preparing payment...' });

    try {
      // Load Razorpay script first
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        throw new Error('Failed to load payment system');
      }

      // Create subscription
      const result = await createSubscription(billingCycle, region);
      
      if (result.success && result.subscriptionId) {
        setMessage({ type: 'info', text: 'Opening payment window...' });
        
        // Open Razorpay Checkout popup
        openRazorpayCheckout(
          result,
          // On Success
          async (paymentData) => {
            setMessage({ type: 'info', text: 'Payment successful! Activating your subscription...' });
            
            try {
              // Verify and activate subscription
              const activateResult = await activateRazorpaySubscription(paymentData);
              
              if (activateResult.success) {
                await fetchUserProfile(user.uid);
                setMessage({ type: 'success', text: 'Welcome to Pro! Your subscription is now active.' });
                setShowSuccessView(true);
              } else {
                setMessage({ type: 'error', text: 'Payment received but activation failed. Please contact support.' });
              }
            } catch (error) {
              console.error('Activation error:', error);
              setMessage({ type: 'error', text: 'Payment received but activation failed. Please contact support.' });
            }
            setLoading(false);
          },
          // On Error
          (error) => {
            console.error('Payment error:', error);
            setMessage({ type: 'error', text: `Payment failed: ${error.description || 'Please try again.'}` });
            setLoading(false);
          },
          // On Close (popup dismissed without payment)
          () => {
            setMessage({ type: 'info', text: 'Payment cancelled. You can try again anytime.' });
            setLoading(false);
          }
        );
      } else {
        setMessage({ 
          type: 'error', 
          text: result.error || 'Failed to create subscription. Please try again.' 
        });
        setLoading(false);
      }
    } catch (error) {
      console.error('Subscription error:', error);
      setMessage({ type: 'error', text: 'Something went wrong. Please try again.' });
      setLoading(false);
    }
  };

  // Customers outside India pay through PayPal: send them to PayPal's approval page.
  const handlePayPalSubscribe = async () => {
    if (!user) {
      navigate('/signup?redirect=/pricing');
      return;
    }
    if (isPro) {
      setMessage({ type: 'info', text: 'You already have an active Pro subscription!' });
      return;
    }
    setLoading(true);
    setMessage({ type: 'info', text: 'Taking you to PayPal…' });
    try {
      window.location.href = await startPayPalCheckout(billingCycle);
    } catch (error) {
      console.error('PayPal checkout error:', error);
      setMessage({ type: 'error', text: error.code === 'functions/already-exists' ? error.message : 'Could not start PayPal checkout. Please try again.' });
      setLoading(false);
    }
  };

  const handleSubscribe = region === 'international' ? handlePayPalSubscribe : handleRazorpaySubscribe;

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will have access until the end of your current billing period.')) {
      return;
    }

    setCancelLoading(true);
    setMessage(null);

    try {
      let result;
      
      // Check which payment provider was used
      if (userProfile?.paymentProvider === 'paypal') {
        result = await cancelPayPalSubscription(userProfile.subscriptionId);
      } else {
        result = await cancelSubscription();
      }
      
      if (result.success) {
        setMessage({ type: 'success', text: result.message });
        await fetchUserProfile(user.uid);
      } else {
        setMessage({ type: 'error', text: result.error || 'Failed to cancel subscription.' });
      }
    } catch (error) {
      console.error('Cancel error:', error);
      setMessage({ type: 'error', text: 'Failed to cancel subscription. Please contact support.' });
    } finally {
      setCancelLoading(false);
    }
  };

  const handleDowngrade = () => {
    setMessage({ 
      type: 'info', 
      text: 'To downgrade, please cancel your subscription first. Your plan will change to Free at the end of your billing period.' 
    });
  };

  const features = plan => (
    <ul className="pricing-features">
      {plan.features.map(feature => (
        <li key={feature.text} className={feature.included ? '' : 'excluded'}>{feature.text}</li>
      ))}
    </ul>
  );

  return (
    <div className="sl-page pricing-page">
      <div className="pageheader borderleft">
        <Breadcrumbs items={[{ label: 'Home', to: '/' }, { label: 'Pricing' }]} />
        <div className="pageheader-row">
          <div className="pageheading">Pricing</div>
        </div>
      </div>

      <div className="borderleft">
        {(showSuccessView || isPro) && !viewingPlans ? (
          <section className="sl-band">
            <span className="sl-eyebrow">{showSuccessView ? 'Welcome to Pro' : 'Your plan'}</span>
            <p className="sl-tagline">
              {showSuccessView
                ? 'Your Pro subscription is active. Everything is unlocked.'
                : 'You’re on SoapLab Pro, with access to every feature.'}
            </p>

            <div className="sl-rule-grid pricing-facts">
              <div>
                <span className="sl-figure-label">Plan</span>
                <strong>Pro, {userProfile?.billingCycle === 'yearly' ? 'yearly' : 'monthly'}</strong>
              </div>
              {subscriptionEndDate && (
                <div>
                  <span className="sl-figure-label">Next billing date</span>
                  <strong>{subscriptionEndDate.toLocaleDateString()}</strong>
                </div>
              )}
              <div>
                <span className="sl-figure-label">Paid through</span>
                <strong>{userProfile?.paymentProvider === 'paypal' ? 'PayPal' : 'Razorpay'}</strong>
              </div>
            </div>

            <div className="sl-actions">
              <button className="sl-btn sl-btn-primary" onClick={() => navigate('/dashboard')}>Go to your dashboard</button>
              <button className="sl-arrow-link pricing-link-btn" onClick={() => { setShowSuccessView(false); setViewingPlans(true); }}>View plans</button>
            </div>
            <button className="pricing-cancel" onClick={handleCancelSubscription} disabled={cancelLoading}>
              {cancelLoading ? 'Processing…' : 'Cancel subscription'}
            </button>
          </section>
        ) : (
          <>
            <section className="sl-band">
              <p className="sl-tagline">
                Start free. Every plan includes the lye calculator, the melt &amp; pour calculator and the
                community. Pro removes the limits and adds the business tools.
              </p>
              <div className="pricing-cycle" role="group" aria-label="Billing period">
                <button className={billingCycle === 'monthly' ? 'active' : ''} onClick={() => setBillingCycle('monthly')}>Monthly</button>
                <button className={billingCycle === 'yearly' ? 'active' : ''} onClick={() => setBillingCycle('yearly')}>
                  Yearly <span>save 16%</span>
                </button>
              </div>
              {message && <div className={`pricing-message ${message.type}`}>{message.text}</div>}
            </section>

            <section className="sl-band">
              <div className="sl-rule-grid cols-2 pricing-plans">
                <div>
                  <h2>{PLANS.free.name}</h2>
                  <p className="pricing-plan-desc">{PLANS.free.description}</p>
                  <span className="sl-figure">{getPricing().monthly.currency}0</span>
                  <span className="sl-figure-label">forever</span>
                  {features(PLANS.free)}
                  {user ? (
                    <button
                      className="sl-btn sl-btn-secondary"
                      onClick={handleDowngrade}
                      disabled={currentPlan === 'free' || loading}
                    >
                      {currentPlan === 'free' ? 'Current plan' : 'Downgrade'}
                    </button>
                  ) : (
                    <Link to="/signup" className="sl-btn sl-btn-secondary">Start free</Link>
                  )}
                </div>

                <div className="pricing-plan-pro">
                  <h2>{PLANS.pro.name} <span className="pricing-tag">{proOnSale ? 'Most popular' : 'Opening soon'}</span></h2>
                  <p className="pricing-plan-desc">{PLANS.pro.description}</p>
                  <span className="sl-figure">{getPrice('pro')}</span>
                  <span className="sl-figure-label">
                    {billingCycle === 'yearly' ? `per year, ${getMonthlyEquivalent()} a month` : 'per month'}
                  </span>
                  {features(PLANS.pro)}
                  {currentPlan === 'pro' && subscriptionStatus === 'active' ? (
                    <button className="sl-btn sl-btn-secondary" disabled>Current plan</button>
                  ) : proOnSale ? (
                    <>
                      <button className="sl-btn sl-btn-primary" onClick={handleSubscribe} disabled={loading}>
                        {loading ? 'Processing…' : user ? 'Subscribe now' : 'Sign in to subscribe'}
                      </button>
                      <p className="pricing-note">
                        {region === 'international'
                          ? 'Secure payment through PayPal, by card or PayPal balance, in US dollars.'
                          : 'Secure payment by UPI, card or netbanking.'}
                      </p>
                    </>
                  ) : (
                    <>
                      <button className="sl-btn sl-btn-secondary" disabled>Opening soon in India</button>
                      <p className="pricing-note">
                        Pro is opening soon for customers in India. Start with the free plan; everything you save carries over.
                      </p>
                    </>
                  )}
                </div>
              </div>
            </section>

            <section className="sl-band">
              <h2 className="sl-subtitle">Compare plans</h2>
              <table className="pricing-table">
                <thead>
                  <tr>
                    <th>Feature</th>
                    <th>Free</th>
                    <th>Pro</th>
                  </tr>
                </thead>
                <tbody>
                  {FEATURE_COMPARISON.map(row => (
                    <tr key={row.feature}>
                      <td>{row.feature}</td>
                      <td>{row.free}</td>
                      <td>{row.pro}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>

            <section className="sl-band">
              <h2 className="sl-subtitle">Questions</h2>
              <div className="sl-rule-grid cols-2">
                <div>
                  <h3>Can I cancel any time?</h3>
                  <p>Yes. Cancel your Pro subscription whenever you like and keep Pro until the end of the period you paid for.</p>
                </div>
                <div>
                  <h3>What payment methods do you accept?</h3>
                  <p>Outside India, you pay through PayPal, by card or PayPal balance, in US dollars. Pro for customers in India opens soon, with UPI, card and netbanking in rupees.</p>
                </div>
                <div>
                  <h3>Do you offer refunds?</h3>
                  <p>Yes. Ask within 7 days of any payment for a full refund. See our <Link to="/refund-policy">Cancellation &amp; Refund Policy</Link>.</p>
                </div>
                <div>
                  <h3>Can I switch between monthly and yearly?</h3>
                  <p>Yes. Yearly plans cost about 16% less than paying monthly for 12 months.</p>
                </div>
              </div>
            </section>

            <div className="slstripe stripelink pricing-stripe">
              {user && !proOnSale
                ? 'Pro is opening soon in India. Until then, everything on the free plan is yours to use.'
                : 'Start free, and upgrade only when your business needs more.'}{' '}
              {!user && <Link to="/signup">Create a free account ❯</Link>}
              {user && proOnSale && <button onClick={handleSubscribe}>Upgrade to Pro ❯</button>}
            </div>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}
